import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { toast } from '@gitbook/spine-react';
import { useEventCallback, useLoadingState } from '@gitbook/util-react-hooks';

import { apiClient, apiQuery } from '../../state';
import type { SiteSpaceCustomizationOverrides } from './types';

export type SiteCustomizationContextType = {
    site: api.Site;
    organizationId: string;
    siteSpaceId?: api.SiteSpace['id'];
    settings: api.SiteCustomizationSettings | SiteSpaceCustomizationOverrides;
    updateSettings: (
        input: Partial<api.SiteCustomizationSettings | SiteSpaceCustomizationOverrides>,
        toastInfos: { title: string }
    ) => Promise<void>;
    savingSettings: boolean;
};

const SiteCustomizationContext = React.createContext<SiteCustomizationContextType | null>(null);

/**
 * Provide the customization context for the site or site space.
 *
 * The context is only for settings that can be set at site and
 * site space level, requiring to switch between both settings.
 *
 * For site-level settings only use `SiteSettingsContext`.
 */
export function SiteCustomizationContextProvider(props: {
    site: api.Site;
    siteSpaceId?: api.SiteSpace['id'];
    organizationId: string;
    children: React.ReactNode;
}) {
    const { site, siteSpaceId, organizationId, children } = props;

    const { data: settings } = useSuspenseQuery(
        // Fetch either site or site space settings
        siteSpaceId
            ? apiQuery('getSiteSpaceRawCustomizationById', {
                  organizationId,
                  siteId: site.id,
                  siteSpaceId,
              })
            : apiQuery('getSiteRawCustomizationById', { organizationId, siteId: site.id })
    );

    const [savingSettings, wrapSavingSettings] = useLoadingState();
    const updateSettings = useEventCallback(async (input, toastInfos) => {
        return wrapSavingSettings(async () => {
            await toast.catch(async () => {
                if (siteSpaceId) {
                    await apiClient.siteCustomization.overrideSiteSpaceCustomizationById(
                        organizationId,
                        site.id,
                        siteSpaceId,
                        {
                            ...settings,
                            ...input,
                        }
                    );
                } else {
                    await apiClient.siteCustomization.updateSiteCustomizationById(
                        organizationId,
                        site.id,
                        {
                            ...settings,
                            ...input,
                        }
                    );
                }

                toast.success(toastInfos.title, {
                    description: 'Your site changes will be reflected soon.',
                });
            }, 'Error while updating site settings');
        });
    });

    const value = React.useMemo(
        () => ({
            site,
            organizationId,
            siteSpaceId,
            settings,
            updateSettings,
            savingSettings,
        }),
        [site, organizationId, siteSpaceId, settings, savingSettings, updateSettings]
    );
    return (
        <SiteCustomizationContext.Provider value={value}>
            {children}
        </SiteCustomizationContext.Provider>
    );
}

/**
 * Hook to access the site customization context.
 */
export function useSiteCustomizationContext() {
    const ctx = React.useContext(SiteCustomizationContext);
    if (!ctx) {
        throw new Error(
            'useSiteCustomizationContext must be used within a SiteCustomizationContextProvider'
        );
    }
    return ctx;
}
